import { emailApiService } from './email/emailApiService';
import { parseDocument } from '../../parsing/services/parseDocService';
import { ParsedOffer, ParseStatus } from '../contexts/BatchParsingContext';
import { validateParsedOfferSnakeCase } from './parsedOfferValidation';
import { fillContractYearsAcrossTerms } from '../lib/steppedAgreementTerms';
import pLimit from 'p-limit';

const CONCURRENCY_LIMIT = 3;
const MAX_RETRIES = 2;

export type SectionKey = 'energy_rates' | 'contract_terms' | 'environmental_charges' | 'non_energy_charges';

export async function batchParseOffers(
    offers: ParsedOffer[],
    onProgress: (offerId: string, progress: number, status: ParseStatus, data?: any, error?: string, warnings?: string[]) => void,
    signal?: AbortSignal,
    concurrency = CONCURRENCY_LIMIT
): Promise<void> {
    const limit = pLimit(concurrency);

    const parsePromises = offers.map(offer =>
        limit(() => {
            if (signal?.aborted) throw new DOMException('Parsing cancelled', 'AbortError');
            return parseOffer(offer, onProgress, signal);
        })
    );

    await Promise.all(parsePromises);
}

async function parseOffer(
    offer: ParsedOffer,
    onProgress: (offerId: string, progress: number, status: ParseStatus, data?: any, error?: string, warnings?: string[]) => void,
    signal?: AbortSignal,
    retryCount = 0
): Promise<void> {
    // Captured so the catch block below can still see what the LLM actually returned on this
    // attempt, even though the value is normally scoped to the `if (result.json)` block below.
    // Without this, a validation failure (e.g. "no usable rate data") threw away the raw JSON
    // entirely - whoever needed to tune the prompt next had nothing but the error string to
    // go on (Cluster I: this is exactly what happened with the ActewAGL "no contract_terms
    // found" failures, which recurred with no visibility into what the LLM actually returned).
    let lastParsedJsonForDebug: any = undefined;

    try {
        if (signal?.aborted) throw new DOMException('Parsing cancelled', 'AbortError');

        onProgress(offer.id, 10, 'parsing');
        console.log(`🔄 Parsing offer: ${offer.fileName}`);

        const blob = await emailApiService.downloadAttachment(offer.attachmentId, offer.accountId);
        const file = new File([blob], offer.fileName, { type: blob.type });

        if (signal?.aborted) throw new DOMException('Parsing cancelled', 'AbortError');
        onProgress(offer.id, 30, 'parsing');

        const result = await parseDocument(file, { retailerId: offer.assignment.retailerId });

        if (signal?.aborted) throw new DOMException('Parsing cancelled', 'AbortError');
        onProgress(offer.id, 70, 'parsing');

        if (result.json) {
            const normalised = normaliseParsedJson(result.json);
            lastParsedJsonForDebug = normalised;

            const { valid, warnings } = validateParsedOfferSnakeCase(normalised);
            if (!valid) {
                // Zero usable rate data — surface as a hard error rather than a
                // silently blank/broken offer card. Existing retry logic below
                // will re-attempt the parse (LLM output can differ between calls).
                throw new Error(`Parsed offer has no usable rate data: ${warnings.join('; ')}`);
            }
            // The server runs the same structural checks PLUS a cross-offer one this side
            // cannot do (it needs the retailer's other offers from the database) — that is
            // what catches a charge the parser dropped. Merge both, de-duplicated, and hand
            // them to the UI: a warning only reaching the console is a warning nobody sees.
            const allWarnings = [...new Set([...(result.jsonWarnings ?? []), ...warnings])];
            if (allWarnings.length > 0) {
                console.warn(`⚠️ Parsed with warnings: ${offer.fileName}`, allWarnings);
            }

            console.log(`✅ Successfully parsed: ${offer.fileName}`);
            onProgress(offer.id, 100, 'completed', normalised, undefined, allWarnings);
        } else {
            throw new Error(result.jsonError || 'Failed to extract JSON from document');
        }
    } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
            console.log(`⚠️ Cancelled parsing: ${offer.fileName}`);
            onProgress(offer.id, 0, 'cancelled');
            return;
        }

        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        if (retryCount < MAX_RETRIES) {
            console.log(`🔁 Retrying parse for offer ${offer.fileName}, attempt ${retryCount + 1}/${MAX_RETRIES}`);
            await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
            return parseOffer(offer, onProgress, signal, retryCount + 1);
        }

        console.error(`❌ Failed to parse: ${offer.fileName} - ${errorMessage}`, lastParsedJsonForDebug ? { rawLlmOutput: lastParsedJsonForDebug } : '(no JSON was returned by the LLM)');
        // NOTE: deliberately NOT passed as `data` here (stays undefined, as before) — that
        // field is written straight into offer.parsedData by updateParseProgress(), and
        // EmailProcessingStep's handleProceedToOffers() treats any offer with a truthy
        // parsedData as ready to convert into a real Offer regardless of status. Passing the
        // failed JSON through that field would let invalid/failed parses silently get
        // converted as if they'd succeeded. The console.error above is the safe way to expose
        // it for debugging without touching the offer's actual parsedData.
        onProgress(offer.id, 0, 'error', undefined, errorMessage);
    }
}

/**
 * Re-parse a single section of an already-parsed offer.
 * Downloads the attachment again and calls /parse with a section-scoped prompt.
 * Returns the new section data merged into existingData.
 */
export async function reparseOfferSection(
    offer: ParsedOffer,
    section: SectionKey,
    existingData: any,
    signal?: AbortSignal
): Promise<any> {
    const blob = await emailApiService.downloadAttachment(offer.attachmentId, offer.accountId);
    if (signal?.aborted) throw new DOMException('Cancelled', 'AbortError');
    const file = new File([blob], offer.fileName, { type: blob.type });

    const result = await parseDocument(file, {
        retailerId: offer.assignment.retailerId,
        section,
        signal,
    });

    if (!result.json) throw new Error(result.jsonError || 'Failed to extract section JSON');

    const normalised = normaliseParsedJson(result.json);
    return mergeSectionData(existingData, section, normalised);
}

// Replace contract_terms per rate-type family (plain keys = Smooth, "_stepped"-suffixed = Stepped)
// instead of nuking the whole object. A section reparse re-extracts ALL terms present in the
// document, so a stale key belonging to a family the reparse DID touch should not survive
// (that's the "reclassified Mixed → pure Smooth, old _stepped lingers" bug — Cluster B). But some
// retailers (e.g. NBE) always show a Stepped table AND a Smooth table side by side — a single
// section-reparse call can incompletely extract just one family. Nuking the whole object then
// silently deletes a rate type that's still genuinely present in the source document. Merging
// per-family fixes both: a touched family gets fully replaced (kills its stale entries), an
// untouched family is left alone (its data survives an incomplete reparse of the other family).
function mergeContractTermsByFamily(existingTerms: Record<string, any> | undefined, freshTerms: Record<string, any> | undefined): Record<string, any> {
    const existing = existingTerms || {};
    const fresh = freshTerms || {};
    if (Object.keys(fresh).length === 0) return existing;

    const isStepped = (key: string) => key.includes('_stepped');
    const freshHasStepped = Object.keys(fresh).some(isStepped);
    const freshHasSmooth = Object.keys(fresh).some(k => !isStepped(k));

    const merged: Record<string, any> = {};
    for (const [key, value] of Object.entries(existing)) {
        const keyIsStepped = isStepped(key);
        // Keep existing entries only from families the fresh reparse didn't touch at all.
        if ((keyIsStepped && !freshHasStepped) || (!keyIsStepped && !freshHasSmooth)) {
            merged[key] = value;
        }
    }
    return { ...merged, ...fresh };
}

/**
 * available_terms for a merged contract_terms, listing exactly the terms that survived.
 *
 * Taking the fresh section's available_terms wholesale undid the per-family merge above: a
 * reparse that extracted only the Stepped family kept both families in contract_terms but left
 * available_terms naming the Stepped ones alone, and the review screen builds its term tabs
 * from available_terms. The result was an offer that still HELD its Smooth terms while showing
 * only Stepped — EnergyAustralia's SOLARSIGNS quote, whose document lists "Stepped 1-5 year"
 * beside "Period 1-5 year", displayed as Stepped-only.
 *
 * Deriving it from the merged keys makes the two agree by construction, so neither can name a
 * term the other does not have. Order follows the fresh section's listing where it overlaps,
 * so a reparse can still reorder terms.
 */
function availableTermsForMerged(mergedTerms: Record<string, any>, freshListed: unknown): string[] {
    const keys = Object.keys(mergedTerms || {});
    const fresh = (Array.isArray(freshListed) ? freshListed : []).map(String);
    const ordered = fresh.filter(t => keys.includes(t));
    return [...ordered, ...keys.filter(k => !ordered.includes(k))];
}

function mergeSectionData(existing: any, section: SectionKey, sectionData: any): any {
    switch (section) {
        case 'energy_rates': {
            const contract_terms = mergeContractTermsByFamily(existing.contract_terms, sectionData.contract_terms);
            return {
                ...existing,
                available_terms: availableTermsForMerged(contract_terms, sectionData.available_terms),
                contract_terms,
            };
        }
        case 'contract_terms': {
            const contract_terms = mergeContractTermsByFamily(existing.contract_terms, sectionData.contract_terms);
            return {
                ...existing,
                available_terms: availableTermsForMerged(contract_terms, sectionData.available_terms),
                contract_terms,
                expiry_date: sectionData.expiry_date ?? existing.expiry_date,
                validity_date: sectionData.validity_date ?? existing.validity_date,
            };
        }
        case 'environmental_charges':
            // Every retailer's environmental_charges SECTION prompt outputs this field at the
            // TOP level (per docs/*-parsing-prompts.md OUTPUT schemas) — not nested under
            // "smooth" the way the FULL parse output is. Reading sectionData.smooth?.* here
            // was always undefined for a section reparse, silently discarding the fresh data
            // and keeping the stale existing value with no error shown.
            return {
                ...existing,
                smooth: {
                    ...(existing.smooth || {}),
                    environmental_charges: sectionData.environmental_charges
                        ?? sectionData.smooth?.environmental_charges
                        ?? existing.smooth?.environmental_charges,
                },
            };
        case 'non_energy_charges':
            // Same top-level-vs-nested mismatch as environmental_charges above.
            return {
                ...existing,
                smooth: {
                    ...(existing.smooth || {}),
                    non_energy_charges: sectionData.non_energy_charges
                        ?? sectionData.smooth?.non_energy_charges
                        ?? existing.smooth?.non_energy_charges,
                },
            };
        default:
            return existing;
    }
}

/**
 * ≥15 leftover days → round up, <15 → keep base.
 * e.g. 12m 11d → 12, 12m 15d → 13, 11m 22d → 12.
 */
function roundMonthsFromRange(startStr: string, endStr: string): number {
    const s = new Date(startStr);
    const e = new Date(endStr);
    let base = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth());
    let leftover = e.getDate() - s.getDate();
    if (leftover < 0) { base -= 1; leftover += 30; }
    return leftover >= 15 ? base + 1 : base;
}

/**
 * Normalise contract_terms keys so partial-month terms use the ≥15-day rounding rule.
 * Also fixes available_terms to match the renamed keys.
 */
function normaliseParsedJson(json: any): any {
    if (!json || typeof json !== 'object') return json;
    const rawTerms: Record<string, any> = json.contract_terms || {};
    if (Object.keys(rawTerms).length === 0) return json;

    const normalisedTerms: Record<string, any> = {};
    const keyMap: Record<string, string> = {};

    for (const [key, termData] of Object.entries(rawTerms)) {
        const prefixStr = key.split('_')[0];
        const suffix    = key.slice(prefixStr.length); // '' | '_smooth' | '_stepped'
        const prefixNum = parseInt(prefixStr, 10);
        const range     = (termData as any)?.date_range;

        if (!isNaN(prefixNum) && range?.start_date && range?.end_date) {
            const corrected = roundMonthsFromRange(range.start_date, range.end_date);
            if (corrected !== prefixNum) {
                console.log(`📅 [batchParse] Term rounding: ${prefixNum}${suffix} → ${corrected}${suffix} (${range.start_date} – ${range.end_date})`);
            }
            const newKey = corrected + suffix;
            if (normalisedTerms[newKey] !== undefined && newKey !== key) {
                console.log(`⚠️ [batchParse] Key collision avoided: keeping "${key}" (would have overwritten "${newKey}")`);
                normalisedTerms[key] = termData;
                keyMap[key] = key;
            } else {
                normalisedTerms[newKey] = termData;
                keyMap[key] = newKey;
            }
        } else {
            normalisedTerms[key] = termData;
            keyMap[key] = key;
        }
    }

    const oldAvailable: string[] = json.available_terms || [];
    const newAvailable = oldAvailable.map(t => keyMap[t] || t);

    // Reconstruct any contract year the parse left unpriced from the SAME OFFER's other terms.
    //
    // ⚠️ THIS HAS TO HAPPEN HERE, NOT ONLY IN THE CALCULATION.
    //
    // Origin's schedule lists one escalating agreement, and the parse can scatter it — each
    // term ending up with a single priced year and every other slot null (prod tender fa2a96e5:
    // 15 -> AY1, 27 -> AY2, 39 -> AY3, 51 -> AY4). calculationRoutes already fills those before
    // pricing, so the money came out right, but the STORED data stayed full of holes and the
    // Stage 3 review screen showed blank Year 2 / Year 3 rate cells — the reviewer could see
    // nothing to check. Filling at normalisation makes the stored offer complete, so the review
    // screen, the offer cards and the calculation all read the same rates.
    //
    // Safe by construction: fillContractYearsAcrossTerms only adopts a value when EVERY term
    // that quotes that contract year quotes the same rate, never overwrites a rate already
    // present, and never adds a slot beyond the term's own year count. A year no term quotes
    // stays unpriced, which is what keeps a genuinely missing agreement year visible.
    const filledTerms = fillContractYearsAcrossTerms(normalisedTerms);

    return { ...json, contract_terms: filledTerms, available_terms: newAvailable };
}

/**
 * Internals exposed for tests only. mergeSectionData decides what survives a section reparse,
 * which is exactly the behaviour worth pinning down — a family silently dropped here shows up
 * as a rate type missing from the review screen with nothing to say it went.
 */
export const __testables = { mergeSectionData, mergeContractTermsByFamily, availableTermsForMerged };
